import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { logCampaignActivity } from "@/lib/campaignHelpers";
import { ManagedScheduleStep } from "@/components/managed-campaign/ManagedScheduleStep";
import { formatCapacityError, isCapacityError } from "@/hooks/useAccountCapacity";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info } from "lucide-react";

interface ScheduleValues {
  timezone: string;
  workingHoursStart: number;
  workingHoursEnd: number;
  workingDays: number[];
}

interface EditScheduleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaignId: string;
  campaign: {
    status: string;
    timezone: string | null;
    working_hours_start: number | null;
    working_hours_end: number | null;
    working_days: number[] | null;
    client_id?: string | null;
    max_connections_daily?: number | null;
  };
}

const EDITABLE_STATUSES = ["paused", "draft"];

export function EditScheduleDialog({ open, onOpenChange, campaignId, campaign }: EditScheduleDialogProps) {
  const queryClient = useQueryClient();
  const [values, setValues] = useState<ScheduleValues>({
    timezone: campaign.timezone || "Asia/Kolkata",
    workingHoursStart: campaign.working_hours_start ?? 9,
    workingHoursEnd: campaign.working_hours_end ?? 18,
    workingDays: campaign.working_days ?? [1, 2, 3, 4, 5],
  });

  // Re-seed from the campaign each time the dialog opens
  useEffect(() => {
    if (open) {
      setValues({
        timezone: campaign.timezone || "Asia/Kolkata",
        workingHoursStart: campaign.working_hours_start ?? 9,
        workingHoursEnd: campaign.working_hours_end ?? 18,
        workingDays: campaign.working_days ?? [1, 2, 3, 4, 5],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const validationError =
    values.workingHoursStart >= values.workingHoursEnd
      ? "Start time must be before end time"
      : values.workingDays.length === 0
        ? "Pick at least one working day"
        : null;

  const saveMutation = useMutation({
    mutationFn: async () => {
      // Guard against a resume that happened in another tab
      const { data: fresh, error: statusError } = await supabase
        .from("campaigns")
        .select("status")
        .eq("id", campaignId)
        .single();
      if (statusError) throw statusError;
      if (!fresh || !EDITABLE_STATUSES.includes(fresh.status)) {
        throw new Error("Pause the campaign before changing its schedule.");
      }

      const { error } = await supabase
        .from("campaigns")
        .update({
          timezone: values.timezone,
          working_hours_start: values.workingHoursStart,
          working_hours_end: values.workingHoursEnd,
          working_days: values.workingDays,
        })
        .eq("id", campaignId);
      if (error) throw error;

      const prevTz = campaign.timezone || "Asia/Kolkata";
      const prevStart = campaign.working_hours_start ?? 9;
      const prevEnd = campaign.working_hours_end ?? 18;
      const prevDays = campaign.working_days ?? [1, 2, 3, 4, 5];

      if (prevTz !== values.timezone) {
        await logCampaignActivity(campaignId, "timezone_changed", { from: prevTz, to: values.timezone });
      }
      if (prevStart !== values.workingHoursStart || prevEnd !== values.workingHoursEnd) {
        await logCampaignActivity(campaignId, "working_hours_changed", {
          from: { start: prevStart, end: prevEnd },
          to: { start: values.workingHoursStart, end: values.workingHoursEnd },
        });
      }
      if (JSON.stringify(prevDays) !== JSON.stringify(values.workingDays)) {
        await logCampaignActivity(campaignId, "working_days_changed", { from: prevDays, to: values.workingDays });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign", campaignId] });
      queryClient.invalidateQueries({ queryKey: ["campaign-stats", campaignId] });
      toast.success(
        campaign.status === "draft"
          ? "Schedule updated — it applies when you start the campaign"
          : "Schedule updated — it applies when you resume the campaign",
      );
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(isCapacityError(error) ? formatCapacityError(error) : error.message || "Could not update the schedule");
    },
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !saveMutation.isPending && onOpenChange(o)}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit schedule</DialogTitle>
          <DialogDescription>
            Choose the days, hours and timezone this campaign is allowed to send. Daily limits are managed by your account team.
          </DialogDescription>
        </DialogHeader>

        <ManagedScheduleStep
          data={{ ...values, clientId: campaign.client_id, maxConnectionsDaily: campaign.max_connections_daily || 0 } as any}
          campaignId={campaignId}
          updateData={(updates) => setValues((prev) => ({ ...prev, ...(updates as Partial<ScheduleValues>) }))}
        />

        <Alert className="border-primary/40 bg-primary/5">
          <Info className="h-4 w-4 text-primary" />
          <AlertDescription className="text-sm">
            The new schedule takes effect when the campaign is {campaign.status === "draft" ? "started" : "resumed"}.
          </AlertDescription>
        </Alert>

        {validationError && <p className="text-sm text-destructive">{validationError}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saveMutation.isPending}>
            Cancel
          </Button>
          <Button onClick={() => saveMutation.mutate()} disabled={!!validationError || saveMutation.isPending}>
            {saveMutation.isPending ? "Saving..." : "Save schedule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
